import { type RefObject, useEffect, useEffectEvent } from 'react';

type PossibleTarget<T> = T | null | undefined | RefObject<T | null | undefined>;

// Maps a target to its corresponding event map so listeners are typed correctly.
type EventMapForTarget<T> = T extends Window
  ? WindowEventMap
  : T extends Document
    ? DocumentEventMap
    : T extends HTMLElement
      ? HTMLElementEventMap
      : T extends MediaQueryList
        ? MediaQueryListEventMap
        : Record<string, Event>;

type EventFor<Target extends EventTarget, Type extends keyof EventMapForTarget<Target> & string> = Extract<
  EventMapForTarget<Target>[Type],
  Event
>;

export function useEventListener<Target extends EventTarget, Type extends keyof EventMapForTarget<Target> & string>(
  target: PossibleTarget<Target>,
  type: Type,
  listener: (event: EventFor<Target, Type>) => void,
  options?: AddEventListenerOptions | boolean
): void {
  const invokeListener = useEffectEvent(listener);

  useEffect(() => {
    const resolvedTarget = resolveTarget(target);

    if (!(resolvedTarget?.addEventListener && resolvedTarget?.removeEventListener)) {
      return;
    }

    const eventHandler: EventListener = (event) => {
      const isEventForResolvedTarget = (candidate: Event): candidate is EventFor<Target, Type> =>
        candidate.type === type;
      if (isEventForResolvedTarget(event)) {
        invokeListener(event);
      }
    };

    resolvedTarget.addEventListener(type, eventHandler, options);
    return () => {
      resolvedTarget.removeEventListener(type, eventHandler, options);
    };
    // Effect events remain stable across renders; no dependency required.
  }, [options, target, type]);
}

const isRefObject = <Target extends EventTarget>(
  candidate: PossibleTarget<Target>
): candidate is RefObject<Target | null | undefined> => {
  if (!candidate || typeof candidate !== 'object') {
    return false;
  }

  return 'current' in candidate;
};

const resolveTarget = <Target extends EventTarget>(target: PossibleTarget<Target>): Target | null | undefined => {
  if (!target) {
    return target ?? null;
  }

  if (isRefObject(target)) {
    return target.current ?? null;
  }

  return target;
};
